import { useCallback, useRef, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { setAnswerFor } from './answer-for';
import { setSearchOwnDocs } from './search-own-docs';
import { useAgentUiCommands } from './ui-commands';
import {
  createAgentSession,
  getAgentPendingActions,
  getAgentSession,
  respondAgentAction,
  streamAgentMessage,
} from './agent-api';
import type {
  AgentAttachmentMeta,
  AgentBlock,
  AgentSessionSummary,
  AgentStreamEvent,
  CatalogArtifact,
  OptionalAddition,
} from './agent-api';

// The active full-page conversation, remembered across navigation so returning
// to the assistant resumes it — and, because the backend keeps running a turn
// after the client leaves, shows work that finished while the user was away.
// Callers that want an independent conversation (e.g. the onboarding product
// step) pass their own storageKey so it doesn't collide with the home chat.
export const HOME_SESSION_KEY = 'revvly.agent.home.session';

export interface UseAgentChatOptions {
  /** localStorage key for the persisted active session. Defaults to the home chat. */
  storageKey?: string;
}

function readStored(key: string): string | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function writeStored(key: string, value: string): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(key, value);
  } catch {
    // localStorage may be unavailable (privacy mode) — non-fatal.
  }
}
function removeStored(key: string): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(key);
  } catch {
    // non-fatal
  }
}

const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

// Background-turn poll: how often and how long to wait for a reply that is
// still being generated server-side when the user returns.
const RESUME_POLL_INTERVAL_MS = 2500;
const RESUME_POLL_MAX_TRIES = 48; // ~2 minutes

export interface AgentProposal {
  actionId: string;
  tool: string;
  summary: string;
  input: Record<string, unknown>;
  /** Rich catalog-entity card data, when the proposal creates a bundle/module/component. */
  artifact?: CatalogArtifact;
  /** Optional elements not set yet, shown on the card so the user can add them before approving. */
  optionalAdditions?: OptionalAddition[];
  /** Decline offers an optional "why not?" field; the agent remembers the answer. */
  asksDeclineReason?: boolean;
  status: 'pending' | 'working' | 'confirmed' | 'declined' | 'superseded' | 'failed' | 'uncertain';
  /** Server-owned outcome copy for failed/expired/uncertain external approvals. */
  statusMessage?: string;
}

export interface AgentChatMessage {
  role: 'user' | 'assistant';
  content: string;
  /** Names of tools the assistant is using / used for this turn. */
  toolActivity: string[];
  /** Mutating actions awaiting (or past) user confirmation. */
  proposals: AgentProposal[];
  /** Structured answer cards (metric tiles, charts, tables, choice pickers). */
  blocks: AgentBlock[];
  /** Files attached to this (user) turn, shown as chips on the bubble. */
  attachments?: AgentAttachmentMeta[];
  isError?: boolean;
  /** True while deltas are still arriving for this message. */
  streaming?: boolean;
}

/**
 * Human labels for tool-activity chips ("Searching the documentation…").
 *
 * Deliberately partial — the consumer falls back to the raw tool name, and most
 * of the CRM and task tools have never had an entry. Adding one for every tool
 * is a worthwhile cleanup and not this map's current contract; what matters is
 * that a NEW tool does not grow the unlabelled set.
 */
export const TOOL_LABELS: Record<string, string> = {
  search_docs: 'Searching the documentation',
  search_help_articles: 'Searching your docs',
  query_clients: 'Looking up clients',
  query_subscriptions: 'Checking subscriptions',
  get_billing_schedule: 'Checking the billing schedule',
  query_invoices: 'Checking invoices',
  get_analytics: 'Computing analytics',
  list_regions: 'Looking up regions',
  list_catalog: 'Looking up the catalog',
  get_catalog_prices: 'Looking up prices',
  set_catalog_prices: 'Proposing a price change',
  set_available_periods: 'Proposing a billing-period change',
  offer_choices: 'Offering options',
  offer_pricing_models: 'Offering pricing models',
  explain_pricing_models: 'Comparing pricing models',
  create_client: 'Proposing a new client',
  create_subscription: 'Proposing a subscription',
  create_invoice: 'Proposing an invoice',
  query_discounts: 'Looking up discounts',
  apply_discount: 'Proposing a discount',
  create_bundle: 'Proposing a new bundle',
  create_module: 'Proposing a new module',
  create_metric: 'Proposing a new metric',
  create_variable_component: 'Proposing a variable component',
  create_plan: 'Building your plan',
  create_region: 'Proposing a region',
  publish_catalog_entity: 'Proposing to publish',
  set_task_status: 'Updating a task',
  record_profile_answer: 'Noting your answer',
  get_onboarding_state: 'Going over your answers',
  get_plan_options: 'Finding the plan that fits',
  propose_trial: 'Preparing your trial',
  advance_tour_step: 'Moving the tour on',
  draft_okrs: 'Drafting goals',
  setup_okrs: 'Setting up your goals',
  undo_onboarding_record: 'Preparing the undo',
};

/** Map a persisted session's transcript to render messages (drops tool-result turns). */
function toChatMessages(session: AgentSessionSummary): AgentChatMessage[] {
  return session.transcript
    .filter(
      (turn) =>
        turn.text ||
        turn.toolCalls.length > 0 ||
        (turn.blocks?.length ?? 0) > 0 ||
        (turn.attachments?.length ?? 0) > 0,
    )
    .map((turn) => ({
      role: turn.role,
      content: turn.text,
      toolActivity: turn.toolCalls,
      proposals: [],
      blocks: turn.blocks ?? [],
      ...(turn.attachments && turn.attachments.length > 0 && { attachments: turn.attachments }),
    }));
}

/** A settled assistant answer carries prose or cards (not just tool activity). */
function isAnswer(message: AgentChatMessage | undefined): boolean {
  return (
    !!message && message.role === 'assistant' && (!!message.content || message.blocks.length > 0)
  );
}

/**
 * The conversation stopped short of an answer: the user's message is unanswered,
 * or the assistant's last turn only ran tools (its reply is still to come).
 */
function awaitsReply(message: AgentChatMessage | undefined): boolean {
  if (!message) return false;
  return message.role === 'user' || (!isAnswer(message) && message.toolActivity.length > 0);
}

/**
 * Conversation state for the in-app assistant. The backend owns the
 * transcript (raw blocks in agent_sessions); this hook owns only the
 * render state of the current browser session. One session is created
 * lazily on first send; reset() starts a fresh one; loadSession() resumes
 * a persisted conversation; restore() re-opens the last conversation on mount.
 *
 * The active session id is persisted to localStorage, and the backend keeps a
 * turn running after the client disconnects, so leaving the page mid-turn and
 * coming back shows the work that finished while away (restore() re-loads it and
 * polls for a still-in-flight reply).
 *
 * Mutating tools surface as proposals: confirm/decline hits the action
 * endpoint, and once the round is complete the conversation CONTINUES on
 * a fresh streaming request (stateless confirm/resume).
 */
export function useAgentChat(options?: UseAgentChatOptions) {
  const storageKey = options?.storageKey ?? HOME_SESSION_KEY;
  const { getAccessTokenSilently } = useAuth();
  const ui = useAgentUiCommands();
  const [messages, setMessages] = useState<AgentChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const sessionIdRef = useRef<string | null>(null);
  // Mirror of sessionIdRef as render state, so consumers (e.g. the history
  // rail) can highlight the conversation that is currently loaded.
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);

  // A proposed (mutating) action is still on the table when any proposal is
  // pending/working. Sending a new message is allowed: the backend closes the open
  // cards as superseded (nothing runs) and answers the new message instead.
  const awaitingConfirmation = messages.some((message) =>
    message.proposals.some(
      (proposal) => proposal.status === 'pending' || proposal.status === 'working',
    ),
  );

  // A revision requested via "Add detail" while OTHER proposals in the round are
  // still open: hold the instruction here so it isn't dropped, and replay it as
  // the resume message once the round finally completes (see resumeAfterRound).
  const pendingRevisionRef = useRef<string | null>(null);

  const patchLastAssistant = useCallback(
    (patch: (message: AgentChatMessage) => AgentChatMessage) => {
      setMessages((current) => {
        const next = [...current];
        for (let index = next.length - 1; index >= 0; index--) {
          if (next[index].role === 'assistant') {
            next[index] = patch(next[index]);
            break;
          }
        }
        return next;
      });
    },
    [],
  );

  const consumeEvent = useCallback(
    (event: AgentStreamEvent) => {
      ui.handleEvent(event);
      if (event.type === 'text_delta') {
        patchLastAssistant((message) => ({ ...message, content: message.content + event.text }));
      }
      if (event.type === 'tool_started') {
        patchLastAssistant((message) => ({
          ...message,
          toolActivity: [...message.toolActivity, event.tool],
        }));
      }
      if (event.type === 'structured_block') {
        patchLastAssistant((message) => ({
          ...message,
          blocks: [...message.blocks, event.block],
        }));
      }
      if (event.type === 'action_proposal') {
        patchLastAssistant((message) => ({
          ...message,
          proposals: [
            ...message.proposals,
            {
              actionId: event.actionId,
              tool: event.tool,
              summary: event.summary,
              input: event.input,
              ...(event.artifact && { artifact: event.artifact }),
              ...(event.optionalAdditions && { optionalAdditions: event.optionalAdditions }),
              ...(event.asksDeclineReason && { asksDeclineReason: true }),
              status: 'pending',
            },
          ],
        }));
      }
    },
    [patchLastAssistant, ui],
  );

  const streamTurn = useCallback(
    async (message?: string, attachments?: string[]) => {
      setIsStreaming(true);
      try {
        const { reply } = await streamAgentMessage({
          getToken: getAccessTokenSilently,
          sessionId: sessionIdRef.current as string,
          message,
          attachments,
          onEvent: consumeEvent,
        });
        patchLastAssistant((assistant) => ({
          ...assistant,
          content: reply || assistant.content,
          streaming: false,
        }));
      } catch (error) {
        patchLastAssistant((assistant) => ({
          ...assistant,
          content:
            error instanceof Error && error.message
              ? error.message
              : 'The assistant hit an unexpected error. Please try again.',
          isError: true,
          streaming: false,
        }));
      } finally {
        setIsStreaming(false);
      }
    },
    [consumeEvent, getAccessTokenSilently, patchLastAssistant],
  );

  /**
   * Resume the conversation once a confirmation round is fully resolved. If a
   * revision was requested during the round ("Add detail"), replay its
   * instruction as the resume message (and show it) so the requested element is
   * never lost — even when other proposals kept the round open past the click.
   * Otherwise resume with no message (the flushed tool_results drive the turn).
   */
  const resumeAfterRound = useCallback(async () => {
    if (!sessionIdRef.current) return;
    const instruction = pendingRevisionRef.current;
    pendingRevisionRef.current = null;
    setMessages((current) => [
      ...current,
      ...(instruction
        ? [
            {
              role: 'user' as const,
              content: instruction,
              toolActivity: [],
              proposals: [],
              blocks: [],
            },
          ]
        : []),
      {
        role: 'assistant',
        content: '',
        toolActivity: [],
        proposals: [],
        blocks: [],
        streaming: true,
      },
    ]);
    await streamTurn(instruction ?? undefined);
  }, [streamTurn]);

  const send = useCallback(
    async (text: string, attachments?: AgentAttachmentMeta[]) => {
      const content = text.trim();
      const attachmentIds = (attachments ?? []).map((att) => att.fileId);
      // Require text (an attachment alone is ambiguous) and a settled turn.
      if (!content || isStreaming) return;

      // The new message replaces any card still open; the server does the same.
      setMessages((current) =>
        current.map((message) =>
          message.proposals.some((proposal) => proposal.status === 'pending')
            ? {
                ...message,
                proposals: message.proposals.map((proposal) =>
                  proposal.status === 'pending'
                    ? { ...proposal, status: 'superseded' as const }
                    : proposal,
                ),
              }
            : message,
        ),
      );

      setMessages((current) => [
        ...current,
        {
          role: 'user',
          content,
          toolActivity: [],
          proposals: [],
          blocks: [],
          ...(attachments && attachments.length > 0 && { attachments }),
        },
        {
          role: 'assistant',
          content: '',
          toolActivity: [],
          proposals: [],
          blocks: [],
          streaming: true,
        },
      ]);

      try {
        if (!sessionIdRef.current) {
          const session = await createAgentSession(getAccessTokenSilently);
          sessionIdRef.current = session.id;
          setActiveSessionId(session.id);
          // Remember it so returning to the page resumes this conversation.
          writeStored(storageKey, session.id);
        }
      } catch (error) {
        patchLastAssistant((assistant) => ({
          ...assistant,
          content: error instanceof Error ? error.message : 'Could not start a conversation.',
          isError: true,
          streaming: false,
        }));
        return;
      }
      await streamTurn(content, attachmentIds.length > 0 ? attachmentIds : undefined);
    },
    [getAccessTokenSilently, isStreaming, patchLastAssistant, streamTurn, storageKey],
  );

  /** Confirm or decline a proposed action, then continue the conversation. */
  const respondToAction = useCallback(
    async (actionId: string, decision: 'confirm' | 'reject', reason?: string) => {
      if (isStreaming) return;
      const setStatus = (status: AgentProposal['status']) =>
        setMessages((current) =>
          current.map((message) => ({
            ...message,
            proposals: message.proposals.map((proposal) =>
              proposal.actionId === actionId ? { ...proposal, status } : proposal,
            ),
          })),
        );

      setStatus('working');
      let roundComplete = false;
      try {
        const resolution = await respondAgentAction(
          getAccessTokenSilently,
          actionId,
          decision,
          reason,
        );
        setStatus(resolution.status === 'CONFIRMED' ? 'confirmed' : 'declined');
        // A confirmed write ran in the confirm request, outside the stream:
        // reload the page behind the chat so it shows the change.
        if (resolution.status === 'CONFIRMED') ui.refresh();
        roundComplete = resolution.roundComplete;
      } catch {
        setStatus('failed');
        return;
      }

      if (roundComplete && sessionIdRef.current) {
        // Resume: the confirmed/declined results are in the transcript now. If a
        // revision was queued earlier in this round, resumeAfterRound replays it.
        await resumeAfterRound();
      }
    },
    [getAccessTokenSilently, isStreaming, resumeAfterRound, ui],
  );

  /**
   * Withdraw a pending proposal and ask the agent to re-propose it with one more
   * optional element the user picked from the card. Declines the current action
   * (so the round resolves), then drives a fresh turn with a plain-language
   * instruction — the agent gathers anything it still needs and proposes again.
   * The instruction rides as a normal user message (same shape as answering a
   * choice picker), so it alternates cleanly after the flushed tool_result.
   */
  const reviseProposal = useCallback(
    async (actionId: string, instruction: string) => {
      if (isStreaming) return;
      const setStatus = (status: AgentProposal['status']) =>
        setMessages((current) =>
          current.map((message) => ({
            ...message,
            proposals: message.proposals.map((proposal) =>
              proposal.actionId === actionId ? { ...proposal, status } : proposal,
            ),
          })),
        );

      setStatus('working');
      // Hold the instruction NOW so it survives even if this decline leaves the
      // round open (other proposals still pending) — resumeAfterRound replays it
      // when the round finally completes, instead of the detail being dropped.
      pendingRevisionRef.current = instruction;
      let roundComplete = false;
      try {
        const resolution = await respondAgentAction(getAccessTokenSilently, actionId, 'reject');
        setStatus('declined');
        roundComplete = resolution.roundComplete;
      } catch {
        setStatus('failed');
        pendingRevisionRef.current = null;
        return;
      }

      // Typical case (one write per turn): the round is already complete, so
      // resume immediately with the instruction. Otherwise it stays queued.
      if (roundComplete && sessionIdRef.current) {
        await resumeAfterRound();
      }
    },
    [getAccessTokenSilently, isStreaming, resumeAfterRound],
  );

  /**
   * Re-surface any still-open proposals (fetched from the server) as confirmation
   * cards on the latest assistant turn. Live proposals aren't part of the
   * transcript, so after a reload/navigation the conversation would otherwise be
   * locked "awaiting confirmation" with nothing to act on. Returns whether any
   * were surfaced. Best-effort — a fetch failure is non-blocking.
   */
  const surfacePendingActions = useCallback(
    async (sessionId: string): Promise<boolean> => {
      const pending = await getAgentPendingActions(getAccessTokenSilently, sessionId);
      if (pending.length === 0 || sessionIdRef.current !== sessionId) return false;
      const proposals: AgentProposal[] = pending.map((action) => ({
        actionId: action.actionId,
        tool: action.tool,
        summary: action.summary,
        input: action.input,
        ...(action.artifact && { artifact: action.artifact }),
        ...(action.optionalAdditions && { optionalAdditions: action.optionalAdditions }),
        ...(action.asksDeclineReason && { asksDeclineReason: true }),
        status: 'pending',
      }));
      setMessages((current) => {
        const next = [...current];
        for (let index = next.length - 1; index >= 0; index--) {
          if (next[index].role === 'assistant') {
            next[index] = { ...next[index], proposals: [...next[index].proposals, ...proposals] };
            return next;
          }
        }
        // No assistant turn to hang them on — add one to host the cards.
        next.push({ role: 'assistant', content: '', toolActivity: [], proposals, blocks: [] });
        return next;
      });
      return true;
    },
    [getAccessTokenSilently],
  );

  /** Resume a persisted conversation from the session list. */
  const loadSession = useCallback(
    async (sessionId: string) => {
      const session = await getAgentSession(getAccessTokenSilently, sessionId);
      sessionIdRef.current = session.id;
      setActiveSessionId(session.id);
      writeStored(storageKey, session.id);
      setMessages(toChatMessages(session));
      await surfacePendingActions(session.id);
    },
    [getAccessTokenSilently, surfacePendingActions, storageKey],
  );

  /**
   * Poll a session whose latest turn is a user question with no reply yet — the
   * turn is still running server-side (the user left mid-turn). Shows the working
   * indicator and swaps in the transcript once the answer lands, or gives up
   * after the deadline. No-op if the user navigates on or starts something else.
   */
  const pollForReply = useCallback(
    async (sessionId: string) => {
      setIsStreaming(true);
      setMessages((current) => [
        ...current,
        {
          role: 'assistant',
          content: '',
          toolActivity: [],
          proposals: [],
          blocks: [],
          streaming: true,
        },
      ]);
      try {
        for (let attempt = 0; attempt < RESUME_POLL_MAX_TRIES; attempt++) {
          await delay(RESUME_POLL_INTERVAL_MS);
          if (sessionIdRef.current !== sessionId) return; // user moved on
          let messagesNow: AgentChatMessage[];
          try {
            messagesNow = toChatMessages(await getAgentSession(getAccessTokenSilently, sessionId));
          } catch {
            return; // transient failure — stop quietly, the transcript is intact
          }
          if (isAnswer(messagesNow[messagesNow.length - 1])) {
            setMessages(messagesNow);
            return;
          }
        }
        // Deadline hit: drop the working placeholder, leave the transcript as-is.
        setMessages((current) =>
          current.filter((m) => !(m.role === 'assistant' && m.streaming && !m.content)),
        );
      } finally {
        // Only clear the flag if this poll is still the active session — a newer
        // send()/streamTurn() for a different session may already own isStreaming.
        if (sessionIdRef.current === sessionId) setIsStreaming(false);
      }
    },
    [getAccessTokenSilently],
  );

  /**
   * Re-open the last conversation on mount (called once by the page). If its
   * final turn is a user question still awaiting a reply, poll until the
   * background turn finishes. Safe to call when nothing is stored.
   */
  const restore = useCallback(async () => {
    const stored = readStored(storageKey);
    if (!stored || sessionIdRef.current) return;
    let restored: AgentChatMessage[];
    try {
      const session = await getAgentSession(getAccessTokenSilently, stored);
      restored = toChatMessages(session);
      sessionIdRef.current = session.id;
      setActiveSessionId(session.id);
      setMessages(restored);
    } catch {
      removeStored(storageKey); // gone or not ours — forget it
      return;
    }
    // A proposal left open before the reload blocks the conversation until it's
    // resolved — re-surface its card so the user can act. If that's the state,
    // don't also poll (the turn isn't running, it's waiting on the user).
    if (await surfacePendingActions(stored)) return;
    // Otherwise, if it ended without an answer, a reply is still being
    // generated in the background — wait for it rather than showing a dead end.
    // That is a user question with no reply yet, and equally an assistant turn
    // that only ran tools: a confirmed action's reply is written after the
    // confirmation, and the surface that confirmed it may have moved on (the
    // onboarding hands over to the docked panel the moment a trial is set up).
    if (awaitsReply(restored[restored.length - 1])) {
      await pollForReply(stored);
    }
  }, [getAccessTokenSilently, pollForReply, surfacePendingActions, storageKey]);

  const reset = useCallback(() => {
    sessionIdRef.current = null;
    setActiveSessionId(null);
    setMessages([]);
    removeStored(storageKey);
    // A new Ask Revvly chat starts as the team, not as the customer the last one was for, and
    // with "Your docs" off.
    if (storageKey === HOME_SESSION_KEY) {
      setAnswerFor(null);
      setSearchOwnDocs(false);
    }
  }, [storageKey]);

  /**
   * Adopt whatever conversation another surface sharing this storage key is on
   * (the bubble and the full-page assistant are one conversation). Call when
   * this surface becomes visible again. No-op while this surface is mid-turn,
   * or when it is already on the stored conversation; a cleared key means the
   * other surface started a new chat, so this one starts fresh too.
   */
  const syncFromStorage = useCallback(async () => {
    if (isStreaming) return;
    const stored = readStored(storageKey);
    if (stored === sessionIdRef.current) return;
    sessionIdRef.current = null;
    setActiveSessionId(null);
    setMessages([]);
    if (stored) await restore();
  }, [isStreaming, restore, storageKey]);

  return {
    messages,
    isStreaming,
    awaitingConfirmation,
    activeSessionId,
    send,
    respondToAction,
    reviseProposal,
    loadSession,
    restore,
    reset,
    syncFromStorage,
  };
}
